/**
 * New Request Page
 *
 * Form to create a new request within a project.
 */

import type { Database } from '@cortex/database';
import {
  Alert,
  AlertDescription,
  Button,
  Container,
  Form,
  Grid,
  Heading,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Surface,
  Text,
  TextArea,
} from '@cortex/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { useProjectsDropdown } from '@/hooks/use-project-queries';
import { useCreateRequest } from '@/hooks/use-request-queries';

type RequestType = Database['public']['Enums']['request_type'];
type RequestPriority = Database['public']['Enums']['request_priority'];

const newRequestSearchSchema = z.object({
  projectId: z.string().optional(),
});

export const Route = createFileRoute('/_app/_main/requests/new')({
  validateSearch: newRequestSearchSchema,
  component: NewRequestPage,
});

function NewRequestPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<RequestType>('feature');
  const [priority, setPriority] = useState<RequestPriority>('medium');
  const [selectedProjectId, setSelectedProjectId] = useState(
    search.projectId ?? ''
  );

  // Fetch projects for dropdown via API
  const { data: projects } = useProjectsDropdown();

  // Create request mutation via API
  const createMutation = useCreateRequest();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    if (!title.trim()) return;

    createMutation.mutate(
      {
        projectId: selectedProjectId,
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        priority,
      },
      {
        onSuccess: (data) => {
          navigate({
            to: '/requests/$id',
            params: { id: String(data.request_number) },
          });
        },
      }
    );
  };

  const handleBack = () => {
    if (search.projectId) {
      const project = projects?.find((p) => p.id === search.projectId);
      if (project) {
        navigate({ to: '/projects/$slug', params: { slug: project.slug } });
        return;
      }
    }
    navigate({ to: '/' });
  };

  return (
    <Stack gap="none" className="min-h-screen">
      <Surface as="header" variant="bordered" className="bg-surface-1">
        <Container maxWidth="2xl" className="py-6">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4 flex items-center gap-2 text-base"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Heading level={1} className="text-2xl font-bold">
            New Request
          </Heading>
          <Text color="secondary" className="mt-1">
            Create a new request for your project
          </Text>
        </Container>
      </Surface>

      <Surface as="main">
        <Container maxWidth="2xl" className="py-8">
          <Form onSubmit={handleSubmit} gap="lg">
            {createMutation.error && (
              <Alert variant="destructive">
                <AlertDescription>
                  {createMutation.error.message}
                </AlertDescription>
              </Alert>
            )}

            {/* Project Select */}
            <Stack gap="xs">
              <Label htmlFor="project">Project</Label>
              <Select
                value={selectedProjectId}
                onValueChange={setSelectedProjectId}
                required
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects?.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Stack>

            {/* Title */}
            <Stack gap="xs">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="What do you want to build?"
              />
            </Stack>

            {/* Description */}
            <Stack gap="xs">
              <Label htmlFor="description">Description</Label>
              <TextArea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="mt-1"
                placeholder="Describe your request in detail..."
              />
            </Stack>

            {/* Type & Priority */}
            <Grid columns={2} gap="md">
              <Stack gap="xs">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={type}
                  onValueChange={(v) => setType(v as RequestType)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">Feature</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="enhancement">Enhancement</SelectItem>
                    <SelectItem value="chore">Chore</SelectItem>
                  </SelectContent>
                </Select>
              </Stack>
              <Stack gap="xs">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={priority}
                  onValueChange={(v) => setPriority(v as RequestPriority)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </Stack>
            </Grid>

            <Button
              type="submit"
              loading={createMutation.isPending}
              className="w-full"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Request'}
            </Button>
          </Form>
        </Container>
      </Surface>
    </Stack>
  );
}
